import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Fab,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  Link,
  Tooltip,
  Stack,
  Divider,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import * as Icons from '../constants/icons';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import TurquoiseTorihikisakiDialog from '../components/TurquoiseTorihikisakiDialog';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import { useTurquoiseTorihikisakiTable } from '../hooks/useTurquoiseTorihikisakiTable';
import TurquoiseFilterPanel from '../components/TurquoiseFilterPanel';
import { MatchSearchFilters } from '../hooks/useTurquoiseMatchPreview';
import { ep } from '../api/endpoints';

const TORIHIKISAKI_KBN_MAP: Record<string, { label: string; color: 'primary' | 'warning' | 'success' }> = {
  '1': { label: '得意先', color: 'primary' },
  '2': { label: '仕入先', color: 'warning' },
  '3': { label: '両方先', color: 'success' },
};

// 取引先名クリックで詳細ダイアログ (顧客管理の法人顧客と同じコンポーネント) を開く
const buildColumns = (onOpenDetail: (cd: number | string) => void): Column[] => [
  {
    field: 'torihikisaki_name',
    headerName: '取引先',
    width: 320,
    renderCell: (row) => {
      const kbn = TORIHIKISAKI_KBN_MAP[String(row.torihikisaki_kbn ?? '').trim()];
      return (
        <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem', fontFamily: 'monospace' }}
            >
              {String(row.torihikisaki_cd ?? '').padStart(8, '0')}
            </Typography>
            {kbn && (
              <Chip
                label={kbn.label}
                color={kbn.color}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
          </Box>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => onOpenDetail(row.torihikisaki_cd)}
            sx={{ lineHeight: 1.3, textAlign: 'left', color: 'text.primary', fontWeight: 500, cursor: 'pointer' }}
          >
            {row.torihikisaki_name}
          </Link>
        </Box>
      );
    },
  },
  { field: 'torihikisaki_kname', headerName: 'フリガナ', width: 200 },
  {
    field: 'address1',
    headerName: '住所',
    width: 360,
    renderCell: (row) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.25 }}>
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2, fontSize: '0.7rem', fontFamily: 'monospace' }}
        >
          {row.post_no || ' '}
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
          {row.address1}
        </Typography>
      </Box>
    ),
  },
  { field: 'tel_no', headerName: '電話番号', width: 130 },
  { field: 'upd_date', headerName: '更新日時', width: 150 },
];

/**
 * フリーワード検索欄 + 詳細検索。
 * 入力中の文字列はこのコンポーネント内に閉じ込め、Enter / 虫めがね / クリア /
 * 詳細検索の実行時だけ親へ通知する (1 文字ごとに 50 行のテーブルが再描画されるのを防ぐ)。
 * 詳細検索の実行時は入力欄の現在値もあわせて渡す
 * (入力を消しただけでは適用済みのワードが残り、AND 条件になってしまうため)。
 */
const TorihikisakiSearchField = React.memo(function TorihikisakiSearchField({
  filters,
  onSearch,
  onApplyFilters,
  disabled,
}: {
  filters: MatchSearchFilters;
  onSearch: (word: string) => void;
  onApplyFilters: (next: MatchSearchFilters, word: string) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');

  const search = () => onSearch(draft.trim());
  const clear = () => {
    setDraft('');
    onSearch('');
  };

  return (
    <TextField
      size="small"
      placeholder="取引先名 / フリガナ / 取引先CD"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          search();
        }
      }}
      sx={{ minWidth: 360 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Tooltip title="検索 (Enter)">
              <IconButton size="small" onClick={search} aria-label="検索" sx={{ p: 0.5 }}>
                <Icons.Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <Stack direction="row" spacing={0.25} alignItems="center">
              {draft && (
                <Tooltip title="入力をクリア">
                  <IconButton size="small" onClick={clear} aria-label="検索条件をクリア" sx={{ p: 0.5 }}>
                    <Icons.Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Divider orientation="vertical" flexItem sx={{ my: 0.75 }} />
              <TurquoiseFilterPanel
                filters={filters}
                onApply={(next) => onApplyFilters(next, draft.trim())}
                disabled={disabled}
                variant="icon"
                showMakeDate
              />
            </Stack>
          </InputAdornment>
        ),
      }}
    />
  );
});

export default function TurquoiseTorihikisakiManagement() {
  const navigate = useNavigate();
  const {
    data,
    totalCount,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    applySearch,
    applyFilters,
    filters,
    refresh,
  } = useTurquoiseTorihikisakiTable();

  // 詳細ダイアログ (null = 閉じている)
  const [detailCd, setDetailCd] = useState<number | string | null>(null);

  // ミラー最終同期日時 (このページの表示はミラー参照のため鮮度を常時示す)。
  // refresh (更新ボタン) や名寄せレビュー実行後の再取得でも最新化する
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    axios.post(ep('get_turquoise_sync_status'), {})
      .then((res) => { if (!cancelled) setSyncedAt(res.data?.synced_at ?? null); })
      .catch(() => { if (!cancelled) setSyncedAt(null); });
    return () => { cancelled = true; };
  }, [loading]);

  const columns = useMemo(() => buildColumns((cd) => setDetailCd(cd)), []);

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 3, width: '95%', margin: '0 auto' }}>
      {/* CssBaseline 未使用のため border-box を明示 (無いと px 分はみ出し右端のボタンが切れる) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', px: 2, boxSizing: 'border-box' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
            <Icons.RealEstateAgent /> ターコイズ取引先一覧
          </Typography>
          {syncedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              データは {syncedAt} 時点（毎朝6時と名寄せレビュー実行時に自動更新）
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* 検索に関する操作は入力欄へ集約する (入力状態は子に閉じ込め、確定時のみ通知) */}
          <TorihikisakiSearchField
            filters={filters}
            onSearch={applySearch}
            onApplyFilters={applyFilters}
            disabled={loading}
          />

          <Tooltip title="更新">
            <span>
              <IconButton onClick={refresh} disabled={loading} aria-label="更新">
                <Icons.Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Fab
            variant="extended"
            onClick={() => navigate('/turquoise/matching-review')}
            sx={{ ...STANDARD_FAB_CTA_SX, flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            <Icons.AccountTree />
            名寄せレビュー
          </Fab>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5, mx: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ ...TABLE_FILL_SX, mt: 1 }}>
        <StandardDataTable
          rows={data}
          columns={columns}
          loading={loading}
          loadingMessage="取引先データを取得中"
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          onSortChange={() => { }}
        />
      </Box>

      <TurquoiseTorihikisakiDialog
        open={detailCd !== null}
        onClose={() => setDetailCd(null)}
        torihikisakiCd={detailCd}
      />
    </Box>
  );
}
